import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import loginImage from '../../assets/login.png';
import { Loading, LoginForm } from '../../components';
import { SKIP_LOGIN_SECOND_STEP } from '../../constantsConfig/constantsConfig';
import { useAuth } from '../../hooks/useAuth';
import packageJson from '../../../../package.json';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const mainContentId = useId();
  const loadingStatusId = useId();
  const authLoadingStatusId = useId();

  // Company page is mandatory before home for authenticated users.
  useEffect(() => {
    if (isAuthenticated && !authLoading && user) {
      navigate('/company', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, user]);

  const handleLoginSuccess = (result?: { redirectToCompanySelection: true }) => {
    if (result?.redirectToCompanySelection) {
      navigate('/company', { state: { fromLogin: true }, replace: true });
    } else {
      navigate('/company', { replace: true });
    }
  };

  const handleLoadingChange = (loading: boolean) => {
    setIsLoading(loading);
  };

  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background-primary"
        role="main"
        aria-busy
        aria-label="Página de login"
      >
        <div className="text-center">
          <div
            id={authLoadingStatusId}
            role="status"
            aria-live="polite"
            aria-label="Verificando autenticação"
          >
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <div
      className="min-h-screen-safe min-w-screen flex items-center justify-center bg-white"
      role="main"
      aria-busy={isLoading}
      aria-label="Página de login"
    >
      <div className="w-full max-w-md md:max-w-5xl flex flex-row items-center justify-center md:shadow-custom-border rounded-none md:rounded-lg h-full md:h-auto md:ml-8 md:mr-8">
        {/* Image */}
        <div className="w-2/3 hidden md:block" role="img" aria-label="Ilustração de login">
          <img
            src={loginImage}
            alt=""
            className="w-full h-[612px] object-cover rounded-l-md"
            aria-hidden="true"
          />
        </div>

        {/* Login Form */}
        <div
          id={mainContentId}
          className="w-full md:w-[448px] h-full md:h-[612px] flex items-center justify-center rounded-none md:rounded-r-md relative md:max-w-[448px]"
          role="region"
          aria-label="Formulário de login"
        >
          {/* Base container layer - Full height on mobile */}
          <div className="w-full h-full md:h-[612px] space-y-6 py-8 px-2 md:px-8 flex flex-col rounded-none md:rounded-l-md mx-auto min-h-screen md:min-h-0 md:w-[448px] md:max-w-[448px]">
            <LoginForm
              onSuccess={handleLoginSuccess}
              onLoadingChange={handleLoadingChange}
              skipSecondStep={SKIP_LOGIN_SECOND_STEP}
            />
          </div>

          {/* Loading overlay */}
          {isLoading && (
            <div
              id={loadingStatusId}
              className="absolute inset-0 w-full h-full md:w-[448px] md:h-[612px] space-y-6 p-6 bg-white bg-opacity-95 flex flex-col justify-center items-center z-10 rounded-none md:rounded-r-md"
              role="status"
              aria-live="polite"
              aria-label="Carregando"
            >
              <Loading />
            </div>
          )}
        </div>
      </div>
      <footer className="fixed bottom-0 left-0 w-full bg-white py-1 text-center text-xs text-gray-500" aria-label="Versão da aplicação">
        v{packageJson.version}
      </footer>
    </div>
  );
};

export default LoginPage;
